import { createContext, useContext, ReactNode } from 'react';
import { useSidebarState } from '../hooks/useSidebarState';

interface SidebarContextType {
  isCollapsed: boolean;
  isMobile: boolean;
  /** The user's own toggle — a persisted preference shared by every arche. */
  toggle: () => void;
  /** The user's own setting — persisted, shared by every arche. */
  setCollapsed: (collapsed: boolean) => void;
  /**
   * A plugin's setting for ITS arche (useFiasChrome): wins while present,
   * never persisted, never another arche's starting state. Optional because
   * older mocks of the state hook predate it.
   */
  overrideCollapsed?: (collapsed: boolean) => void;
  /** Drop the plugin override, back to the user's preference. */
  clearOverride?: () => void;
}

const SidebarContext = createContext<SidebarContextType | null>(null);

export function SidebarProvider({ children }: { children: ReactNode }) {
  const sidebarState = useSidebarState();

  return <SidebarContext.Provider value={sidebarState}>{children}</SidebarContext.Provider>;
}

export function useSidebar() {
  const context = useContext(SidebarContext);
  if (!context) {
    throw new Error('useSidebar must be used within a SidebarProvider');
  }
  return context;
}

/**
 * The sidebar state, or null where no layout provides one. For code that
 * runs both inside the arche layout and on surfaces without a sidebar (the
 * plugin renderer: the arche page, the embed page, previews) — the strict
 * hook above throws there, and "no sidebar" is a normal answer for it.
 */
export function useSidebarOptional(): SidebarContextType | null {
  return useContext(SidebarContext);
}
